import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { OperationAuditEvent } from '@/api/models';
import { formatGeneratedAt } from '@/pages/accounting/statements/statementMeta';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { AUDIT_ACTION_LABEL } from '../../historyMeta';

type ChangeLogProps = {
    events: OperationAuditEvent[];
    isLoading: boolean;
};

/**
 * Who changed how this operation posts, and when.
 *
 * **This is a log of acts, not of diffs.** What changed is answered by the
 * timeline above it, where the frozen line sets can be compared; an event that
 * also carried a field-level delta would be a second, drifting copy of the thing
 * beside it. So a row says "published v3" and stops there.
 *
 * **Three columns — when, who, what — rather than one running sentence.** The
 * question this list gets read with is nearly always about a person or a date,
 * and a column of names can be scanned where the same names buried mid-sentence
 * have to be read.
 *
 * **An empty log is not "nothing happened".** It is a template nobody has
 * touched since the log landed — the log starts when it starts, and backfilling
 * one from version rows would invent actors and timestamps nobody recorded. The
 * empty state says exactly that rather than implying the operation has never
 * been edited.
 */
export const ChangeLog: React.FC<ChangeLogProps> = ({ events, isLoading }) => {
    const { t } = useTranslation('pages');

    return (
        <section
            aria-label={t('operations.history.log.title')}
            className="flex flex-col gap-3"
            {...getNodeTestIdProp('operations-history-log')}
        >
            <span className="text-xs font-medium uppercase tracking-wide text-muted">
                {t('operations.history.log.title')}
            </span>

            {isLoading ? (
                <div className="flex flex-col gap-2">
                    {[0, 1, 2].map((index) => (
                        <div
                            className="h-4 w-72 animate-pulse rounded bg-surface-secondary"
                            key={index}
                        />
                    ))}
                </div>
            ) : events.length === 0 ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('operations-history-log-empty')}
                >
                    {t('operations.history.log.empty')}
                </p>
            ) : (
                <ol className="flex flex-col">
                    {events.map((event) => (
                        <li
                            className="grid grid-cols-[8.5rem_1fr] gap-x-4 gap-y-1 border-b border-separator/60 py-2 text-sm last:border-b-0 sm:grid-cols-[8.5rem_10rem_1fr]"
                            key={event.id}
                            {...getNodeTestIdProp(
                                `operations-history-log-event-${event.id}`,
                            )}
                        >
                            <span className="whitespace-nowrap tabular-nums text-muted">
                                {formatGeneratedAt(new Date(event.at))}
                            </span>
                            <span className="font-medium text-foreground">
                                {event.actorName ??
                                    t('operations.history.log.unknownActor')}
                            </span>
                            <span className="col-span-2 text-muted sm:col-span-1">
                                {t(AUDIT_ACTION_LABEL[event.action], {
                                    version: event.version ?? 0,
                                })}
                            </span>
                        </li>
                    ))}
                </ol>
            )}
        </section>
    );
};
